import { timezoneStore } from "@/api/hooks/useConfig";
import { DatePicker } from "@/components/v2/DatePicker";
import type { PresetKey } from "@/components/v2/DatePicker/constants";
import {
  getComparisonTimeFilter,
  getTimeFilter,
} from "@/components/v2/DatePicker/helpers";
import { PdfPreviewButton } from "@/components/v2/PdfPreview";
import { TenantCategoriesSelector } from "@/components/v2/TenantCategoriesSelector";
import { ViewWithLoader } from "@/components/v2/ViewWithLoader";

import { useCallback, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";

import { BuildingTrendView } from "./components/buildingTrend";
import { HomeHeader } from "./components/header";
import { KeyNumbersPerCategory } from "./components/keyNumbersPerCategory";
import { KPINumbersHomePage } from "./components/kpiNumbers";
import { Leaderboard } from "./components/leaderboard";
import { TenantContribution } from "./components/tenantContribution";
import { WasteStreamDistribution } from "./components/wasteStreamDistribution";
import { WasteStreamsInUse } from "./components/wasteStreamsInUse";
import { HomePageContext } from "./context";

export const HomeV2 = () => {
  const { timezone } = timezoneStore();
  const [dateRange, setDateRange] = useState<DateRange>();
  const [datePreset, setDatePreset] = useState<PresetKey>();
  const [selectedTenantCategoryIds, setSelectedTenantCategoryIds] = useState<
    string[] | null
  >(null);

  const onDateAndPresetChange = useCallback(
    (range: DateRange, preset: PresetKey) => {
      setDateRange(range);
      setDatePreset(preset);
    },
    [],
  );

  const timeFilter = useMemo(() => getTimeFilter(dateRange), [dateRange]);

  const contextValue = useMemo(
    () => ({
      dateRange,
      datePreset,
      timeFilter,
      comparisonTimeFilter: getComparisonTimeFilter({
        datePreset,
        dateRange,
        timezone,
      }),
      selectedTenantCategoryIds,
    }),
    [dateRange, datePreset, timezone, timeFilter, selectedTenantCategoryIds],
  );

  const dataView = useMemo(
    () => (
      <ViewWithLoader isLoading={!selectedTenantCategoryIds}>
        <HomePageContext.Provider value={contextValue}>
          <>
            <div className="mt-6">
              <KPINumbersHomePage />
            </div>

            <div className="mt-6 flex flex-col min-[1201px]:flex-row">
              <div className="w-full min-w-0">
                <BuildingTrendView />
              </div>
              <div className="mt-6 flex min-w-0 flex-col gap-6 min-[1201px]:mt-0 min-[1201px]:ml-6 min-[1201px]:min-w-2/6">
                <div className="flex-1">
                  <TenantContribution />
                </div>
                <div className="flex-1">
                  <KeyNumbersPerCategory />
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col min-[781px]:flex-row min-[781px]:justify-between">
              <div className="min-w-0 min-[781px]:w-1/2">
                <Leaderboard />
              </div>
              <div className="mt-6 min-w-0 min-[781px]:mt-0 min-[781px]:ml-4 min-[781px]:w-1/2">
                <WasteStreamDistribution />
              </div>
            </div>

            <div className="mt-6">
              <WasteStreamsInUse />
            </div>
          </>
        </HomePageContext.Provider>
      </ViewWithLoader>
    ),
    [contextValue, selectedTenantCategoryIds],
  );

  const pdfView = useMemo(
    () => (
      <div className="h-full min-h-[660px] w-full px-[33px] py-[44px]">
        <div className="flex flex-col items-start gap-4 lg:flex-row lg:items-center lg:justify-between">
          <HomeHeader timeFilter={timeFilter} />
          <div className="flex items-center gap-3">
            <TenantCategoriesSelector
              timeFilter={timeFilter}
              value={selectedTenantCategoryIds}
              onChange={setSelectedTenantCategoryIds}
            />
            <DatePicker
              value={dateRange}
              presetValue={datePreset}
              onChange={onDateAndPresetChange}
            />
          </div>
        </div>
        {dataView}
      </div>
    ),
    [
      onDateAndPresetChange,
      dataView,
      dateRange,
      datePreset,
      timeFilter,
      selectedTenantCategoryIds,
    ],
  );

  const view = useMemo(
    () => (
      <div className="h-full min-h-[660px] w-full px-[33px] py-[44px]">
        <div className="flex items-center justify-between">
          <HomeHeader timeFilter={timeFilter} />
          <div className="flex items-center gap-3">
            <TenantCategoriesSelector
              timeFilter={timeFilter}
              value={selectedTenantCategoryIds}
              onChange={setSelectedTenantCategoryIds}
            />
            <DatePicker
              value={dateRange}
              presetValue={datePreset}
              onChange={onDateAndPresetChange}
            />
            <PdfPreviewButton
              exportFileNameKey="homePage:exportFileName"
              buttonTextKey="homePage:previewButtonText"
              pdfView={pdfView}
            />
          </div>
        </div>
        {dataView}
      </div>
    ),
    [
      onDateAndPresetChange,
      dataView,
      dateRange,
      datePreset,
      pdfView,
      timeFilter,
      selectedTenantCategoryIds,
    ],
  );

  return view;
};
